/**
 * The order drawer.
 *
 * One read feeds all of it (`GET /orders/:id/detail`), so the header, the payment section
 * and the timeline are one snapshot rather than three that disagree. It slides in over the
 * list and pushes nothing, so the row it came from is still on screen and still keyboard
 * navigable behind it.
 *
 * Everything that changes an order arrives through `slots`. The sections know how to lay a
 * fact out and nothing about how to change one, which is what lets the read-only shape and
 * the acting shape be the same components.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { Drawer, ErrorState, Pill, Skeleton } from '@/components';
import { ApiProblem } from '@/lib/api';
import { formatMoney, fullTimestamp, relativeAge } from '@/lib/format';
import { useNow } from '@/lib/hooks';
import { useOrderDrawer } from '../queries';
import type { DrawerSection, OrderDrawerDetailView as OrderDrawerDetail } from '../types';
import {
  ActivitySection,
  CustomerSection,
  FlagsSection,
  FulfilmentSection,
  ItemsSection,
  PaymentSection,
} from './sections';
import { PhotoStrip } from './PhotoStrip';

/** Where an action row or an edit control is injected. Empty is a read-only drawer. */
export interface DrawerSlots {
  primary?: (detail: OrderDrawerDetail) => ReactNode;
  payment?: (detail: OrderDrawerDetail) => ReactNode;
  tracking?: (detail: OrderDrawerDetail) => ReactNode;
  items?: (detail: OrderDrawerDetail) => ReactNode;
  customer?: (detail: OrderDrawerDetail) => ReactNode;
  credit?: (detail: OrderDrawerDetail) => ReactNode;
  activity?: (detail: OrderDrawerDetail) => ReactNode;
  flags?: (detail: OrderDrawerDetail) => ReactNode;
}

export interface OrderDrawerProps {
  orderId: number | null;
  onClose: () => void;
  /** Scroll straight to the section a Review action or a flag chip pointed at. */
  section?: DrawerSection | undefined;
  slots?: DrawerSlots;
}

const STATUS_TONE: Readonly<Record<string, 'neutral' | 'ok' | 'warn' | 'crit' | 'info'>> = {
  pending: 'warn',
  'on-hold': 'warn',
  processing: 'info',
  completed: 'info',
  packed: 'info',
  shipped: 'info',
  delivered: 'ok',
  cancelled: 'neutral',
  refunded: 'neutral',
  failed: 'crit',
};

export function OrderDrawer({ orderId, onClose, section, slots = {} }: OrderDrawerProps) {
  const query = useOrderDrawer(orderId);
  const now = useNow(30_000);
  const detail = query.data ?? null;
  const bodyRef = useRef<HTMLDivElement | null>(null);

  /**
   * Land on the section the row pointed at.
   *
   * Review on a flagged row is a promise that the drawer opens where the problem is. It
   * runs once the detail has rendered, because the section does not exist before then.
   */
  useEffect(() => {
    if (!section || !detail) return;
    const target = document.getElementById(`order-section-${section}`);
    // Guarded because scrolling is a convenience and not being able to do it must never
    // take the drawer down. jsdom does not implement scrollIntoView at all, and a drawer
    // that throws on open is a worse bug than one that opens at the top.
    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ block: 'start', behavior: 'auto' });
    }
  }, [detail, section]);

  if (orderId === null) return null;

  const problem = query.error instanceof ApiProblem ? query.error : null;
  const title = detail ? `Order ${detail.header.order_number}` : `Order ${orderId}`;

  return (
    <Drawer open onClose={onClose} title={title} id={`order-${orderId}`}>
      <div ref={bodyRef}>
        {query.isLoading ? (
          <Skeleton shape="card" lines={6} label="Loading the order" />
        ) : problem || !detail ? (
          <ErrorState
            message={problem ? problem.message : 'This order did not load.'}
            requestId={problem ? problem.shortRequestId : null}
            onRetry={() => void query.refetch()}
          />
        ) : (
          <>
            <header className="ord-head">
              <div className="ord-head__line">
                <Pill tone={STATUS_TONE[detail.header.woo_status] ?? 'neutral'}>
                  {detail.header.woo_status}
                </Pill>
                {/* Absent for a packer (redact.ts): no total, and nothing in its place. */}
                {detail.header.total_cents !== undefined ? (
                  <span className="ord-head__total">
                    {formatMoney(detail.header.total_cents, { showCents: true })}
                  </span>
                ) : null}
              </div>
              <div className="ord-head__line">
                <span className="ord-name">{detail.header.customer_name}</span>
                {detail.header.first_order ? (
                  <span className="ord-badge ord-badge--first">FIRST</span>
                ) : null}
                {detail.header.safearrival ? (
                  <span className="ord-badge ord-badge--safe">SafeArrival</span>
                ) : null}
              </div>
              <div className="ord-head__line ord-sub">
                <span title={fullTimestamp(detail.header.placed_at)}>
                  placed {relativeAge(detail.header.placed_at, now)}
                </span>
                <span>. {detail.header.ops_state}</span>
              </div>
              {slots.primary ? slots.primary(detail) : null}
            </header>

            <PaymentSection detail={detail} now={now} actions={slots.payment?.(detail)} />
            <FulfilmentSection
              detail={detail}
              now={now}
              photos={
                <PhotoStrip
                  key={detail.header.order_id}
                  orderId={detail.header.order_id}
                  photos={detail.fulfilment.photos}
                />
              }
              tracking={slots.tracking?.(detail)}
            />
            <ItemsSection detail={detail} actions={slots.items?.(detail)} />
            <CustomerSection
              detail={detail}
              actions={slots.customer?.(detail)}
              creditActions={slots.credit?.(detail)}
            />
            <ActivitySection detail={detail} now={now} composer={slots.activity?.(detail)} />
            <FlagsSection detail={detail} now={now} actions={slots.flags?.(detail)} />
          </>
        )}
      </div>
    </Drawer>
  );
}
